import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import CarCard, { HeroCarIllustration } from '../components/CarCard';
import { Spinner, Button, formatPrice, T } from '../components/UI';

const MAKES = ['Any Make','Volkswagen','BMW','Audi','Toyota','Ford','Mercedes-Benz','Hyundai','Nissan','Kia','Skoda','Volvo','Honda','Land Rover','Mini','Tesla'];
const YEARS = Array.from({ length: 10 }, (_, i) => 2025 - i);
const PRICES = [5000,8000,10000,12000,15000,20000,25000,30000,40000,50000];

/* Picsum atmosphere photos for split hero crossfade */
const HERO_IMGS = [
  { src: 'https://picsum.photos/id/187/1200/1600', alt: 'Frost-covered field at first light — atmosphere', credit: 'Andre Koch' },
  { src: 'https://picsum.photos/id/112/1200/1600', alt: 'Golden grass heads against dark treeline',        credit: 'Zugr' },
  { src: 'https://picsum.photos/id/955/1200/1600', alt: 'Aerial crop rows in green and gold',              credit: 'Matt Benson' },
  { src: 'https://picsum.photos/id/85/1200/1600',  alt: 'Golden field under open sky',                     credit: 'Gozha Net' },
];

const WHY = [
  { n: '01', h: 'Direct UK sourcing',      b: 'We buy from main UK dealers and auctions, cutting out every middleman between the factory gate and your driveway.' },
  { n: '02', h: 'Full Irish registration', b: 'We handle VRT, NCT preparation and registration. Your car arrives road-legal and ready to drive in Ireland.' },
  { n: '03', h: 'Cartell history check',   b: 'Every car we sell includes a full Cartell report — no hidden history, no outstanding finance, no surprises.' },
  { n: '04', h: '30-day warranty',         b: 'Every car leaves our forecourt with a 30-day mechanical warranty. Your peace of mind is part of the price.' },
];

const TESTIMONIALS = [
  { name: 'Ciarán O\'Brien', loc: 'Dublin',  stars: 5, q: 'Northbridge sourced my BMW 5 Series exactly as I wanted — right colour, spec and mileage. Two weeks from request to keys. Faultless.' },
  { name: 'Aoife Gallagher', loc: 'Cork',    stars: 5, q: 'Bought my RAV4 from their forecourt. Honest, no-pressure, everything NCT ready. Exactly as described.' },
  { name: 'Seán Murphy',     loc: 'Galway',  stars: 5, q: 'Used the import service for a specific Golf spec. They found it, brought it over, registered it — I just collected the keys.' },
];

export default function Home() {
  const nav = useNavigate();
  const [stats,   setStats]   = useState({ total: 0, electric: 0, makes: 0 });
  const [recent,  setRecent]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgIdx,  setImgIdx]  = useState(0);
  const [make,    setMake]    = useState('');
  const [minYear, setMinYear] = useState('');
  const [maxPrice,setMaxPrice]= useState('');
  const [tIdx,    setTIdx]    = useState(0);

  /* Crossfade hero images */
  useEffect(() => {
    const t = setInterval(() => setImgIdx(i => (i+1) % HERO_IMGS.length), 5000);
    return () => clearInterval(t);
  }, []);
  /* Rotate testimonials */
  useEffect(() => {
    const t = setInterval(() => setTIdx(i => (i+1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.getStats().then(setStats).catch(() => {});
    api.getListings({ limit: 4, sort: '' })
      .then(d => setRecent(d.listings))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const goSearch = () => {
    const p = new URLSearchParams();
    if (make && make !== 'Any Make') p.set('make', make);
    if (minYear) p.set('minYear', minYear);
    if (maxPrice) p.set('maxPrice', maxPrice);
    nav(`/stock?${p}`);
  };

  const selS = {
    background: 'rgba(232,230,225,0.1)', border: '1px solid rgba(232,230,225,0.25)',
    color: '#fff', padding: '12px 16px', fontSize: 'var(--step-sm)',
    appearance: 'none', cursor: 'pointer', width: '100%', outline: 'none',
    fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em',
  };

  return (
    <div>

      {/* ══════════════ HERO — split stage ══════════════ */}
      <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '100svh', position: 'relative' }}>

        {/* LEFT — sticky atmosphere photo */}
        <div style={{ position: 'sticky', top: 0, height: '100svh', overflow: 'hidden', background: T.ink }}>
          {HERO_IMGS.map((img, i) => (
            <img key={i} src={img.src} alt={img.alt} width="1200" height="1600" loading={i === 0 ? 'eager' : 'lazy'}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: i === imgIdx ? 0.72 : 0, transition: 'opacity 1.2s ease', display: 'block' }} />
          ))}
          {/* Scrim so nav text reads — gradient top */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 160, background: 'linear-gradient(to bottom, rgba(26,25,22,0.6), transparent)' }} />
          {/* Photo credit */}
          <p style={{ position: 'absolute', bottom: 16, left: 16, fontSize: 10, color: 'rgba(232,230,225,0.35)', letterSpacing: '0.05em', fontStyle: 'italic' }}>
            Photo: {HERO_IMGS[imgIdx].credit} — Unsplash
          </p>
          {/* Image dots */}
          <div style={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', gap: 6 }}>
            {HERO_IMGS.map((_, i) => (
              <button key={i} onClick={() => setImgIdx(i)}
                style={{ width: 6, height: 6, borderRadius: '50%', background: i === imgIdx ? T.accent : 'rgba(232,230,225,0.35)', border: 'none', cursor: 'pointer', padding: 0, transition: 'background 0.3s' }} />
            ))}
          </div>
        </div>

        {/* RIGHT — scrolling content column */}
        <div style={{ background: T.bg, paddingTop: 128, paddingBottom: 80, paddingInline: 'clamp(2rem,5vw,5rem)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>
            🇬🇧 UK Import Specialists · Dublin, Ireland
          </p>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 400, lineHeight: 1.08, letterSpacing: '-0.02em', color: T.ink, marginBottom: 28, maxWidth: '14ch' }}>
            The car<br/>you want,<br/><em style={{ color: T.accent }}>from the UK.</em>
          </h1>
          <p style={{ fontSize: 'var(--step-lead)', color: T.muted, lineHeight: 1.55, maxWidth: '36ch', marginBottom: 40 }}>
            We source, import and register quality UK vehicles directly in Ireland. Browse our stock or tell us exactly what you want.
          </p>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, marginBottom: 44, borderTop: `1px solid ${T.surface}`, borderLeft: `1px solid ${T.surface}` }}>
            {[
              { v: stats.total || '60+', l: 'cars in stock' },
              { v: stats.electric || '8+', l: 'electric cars' },
              { v: '100%',               l: 'UK imported' },
            ].map(s => (
              <div key={s.l} style={{ padding: '18px 20px', borderRight: `1px solid ${T.surface}`, borderBottom: `1px solid ${T.surface}` }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem,3vw,2.5rem)', fontWeight: 400, color: T.ink, lineHeight: 1 }}>{s.v}</div>
                <div style={{ fontSize: 'var(--step-xs)', color: T.muted, marginTop: 4, letterSpacing: '0.04em' }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Quick search */}
          <div style={{ borderTop: `1px solid ${T.surface}`, paddingTop: 28 }}>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Quick search</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <div style={{ position: 'relative' }}>
                <select style={selS} value={make} onChange={e => setMake(e.target.value)} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = 'rgba(232,230,225,0.25)'}>
                  {MAKES.map(m => <option key={m} style={{ background: T.ink }}>{m}</option>)}
                </select>
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 10 }}>▼</span>
              </div>
              <div style={{ position: 'relative' }}>
                <select style={selS} value={minYear} onChange={e => setMinYear(e.target.value)} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = 'rgba(232,230,225,0.25)'}>
                  <option value="" style={{ background: T.ink }}>Any year</option>
                  {YEARS.map(y => <option key={y} style={{ background: T.ink }}>{y}</option>)}
                </select>
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 10 }}>▼</span>
              </div>
              <div style={{ position: 'relative' }}>
                <select style={selS} value={maxPrice} onChange={e => setMaxPrice(e.target.value)} onFocus={e => e.target.style.borderColor = T.accent} onBlur={e => e.target.style.borderColor = 'rgba(232,230,225,0.25)'}>
                  <option value="" style={{ background: T.ink }}>Any budget</option>
                  {PRICES.map(p => <option key={p} value={p} style={{ background: T.ink }}>Up to {formatPrice(p)}</option>)}
                </select>
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 10 }}>▼</span>
              </div>
              <button onClick={goSearch}
                style={{ background: T.accent, color: T.accentInk, border: 'none', padding: '12px', fontSize: 'var(--step-xs)', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'background 0.15s', fontFamily: 'inherit' }}
                onMouseEnter={e => e.target.style.background = '#b8943a'}
                onMouseLeave={e => e.target.style.background = T.accent}>
                Search stock →
              </button>
            </div>
            <Link to="/import" style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em', transition: 'color 0.15s' }}
              onMouseEnter={e => e.target.style.color = T.accent}
              onMouseLeave={e => e.target.style.color = T.muted}>
              Can't find it? We'll source it from the UK →
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════ LATEST STOCK ══════════════ */}
      <section style={{ background: T.ink, paddingBlock: 'clamp(60px, 10vw, 120px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 48, paddingBottom: 20, borderBottom: `1px solid rgba(232,230,225,0.12)` }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 400, color: T.bg, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              Latest stock
            </h2>
            <Link to="/stock" style={{ fontSize: 'var(--step-sm)', color: T.accent, letterSpacing: '0.04em', transition: 'opacity 0.15s', fontWeight: 500 }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              View all {stats.total} cars →
            </Link>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><Spinner size={36} /></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
              {recent.map(car => <CarCard key={car.id} car={car} />)}
            </div>
          )}

          <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center' }}>
            <Link to="/stock"><Button variant="outlineGold" size="lg">Browse all stock</Button></Link>
          </div>
        </div>
      </section>

      {/* ══════════════ CAR ILLUSTRATION FEATURE ══════════════ */}
      <section style={{ background: T.surface, paddingBlock: 'clamp(60px, 8vw, 100px)', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>What we import</p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem, 3.5vw, 3rem)', fontWeight: 400, lineHeight: 1.12, letterSpacing: '-0.02em', marginBottom: 24, maxWidth: '18ch' }}>
              All makes, all body types, all budgets.
            </h2>
            <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.7, marginBottom: 36, maxWidth: '42ch' }}>
              From first cars to prestige SUVs, hatchbacks to estates — if it's available in the UK, we can source it. Our network covers over 5,000 UK dealers and five major auction houses.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: T.surface, border: `1px solid ${T.surface}` }}>
              {['Hatchbacks','Saloons','SUVs & 4×4s','Estates','Coupés','Electrics & Hybrids'].map(t => (
                <div key={t} style={{ background: T.bg, padding: '14px 18px', fontSize: 'var(--step-sm)', color: T.muted, letterSpacing: '0.02em' }}>— {t}</div>
              ))}
            </div>
          </div>
          {/* Illustrated car showcase */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {[
              { bodyType: 'SUV',      colour: 'Black'  },
              { bodyType: 'Saloon',   colour: 'Silver' },
              { bodyType: 'Hatchback',colour: 'White'  },
              { bodyType: 'Estate',   colour: 'Blue'   },
            ].map((c, i) => (
              <div key={i} style={{ background: T.bg, padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, border: `1px solid ${T.surface}` }}>
                <HeroCarIllustration bodyType={c.bodyType} colour={c.colour} />
                <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 4 }}>{c.bodyType}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ WHY US — staggered list ══════════════ */}
      <section style={{ paddingBlock: 'clamp(60px, 10vw, 120px)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'clamp(40px, 8vw, 120px)', alignItems: 'start' }}>
            <div style={{ position: 'sticky', top: 100 }}>
              <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>Why Northbridge</p>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 28 }}>
                The import<br />done right.
              </h2>
              <img src="https://picsum.photos/id/107/700/500" alt="Green wheat ears at dawn — atmosphere" width="700" height="500" loading="lazy"
                style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block', filter: 'brightness(0.85) saturate(0.85)' }} />
              <p style={{ fontSize: 10, color: T.muted, marginTop: 6, fontStyle: 'italic' }}>Photo: Lukas Schweizer — Unsplash</p>
            </div>
            <div>
              {WHY.map((w, i) => (
                <div key={i} style={{ paddingBlock: 36, borderTop: `1px solid ${T.surface}`, display: 'grid', gridTemplateColumns: '60px 1fr', gap: 24, alignItems: 'start' }}>
                  <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', color: T.accent, fontWeight: 400 }}>{w.n}</span>
                  <div>
                    <h3 style={{ fontSize: 'var(--step-lead)', fontFamily: 'Playfair Display, serif', fontWeight: 400, letterSpacing: '-0.01em', marginBottom: 10, color: T.ink }}>{w.h}</h3>
                    <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.7, maxWidth: '48ch' }}>{w.b}</p>
                  </div>
                </div>
              ))}
              <div style={{ paddingTop: 36, borderTop: `1px solid ${T.surface}` }}>
                <Link to="/about"><Button variant="dark" size="lg">About Northbridge</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ IMPORT CTA — full-bleed dark ══════════════ */}
      <section style={{ background: T.ink, paddingBlock: 'clamp(60px, 10vw, 120px)', position: 'relative', overflow: 'hidden' }}>
        {/* Background atmosphere image */}
        <img src="https://picsum.photos/id/699/1920/900" alt="" loading="lazy" width="1920" height="900"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.14, display: 'block' }} />
        <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(40px, 8vw, 100px)', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 20 }}>Custom import service</p>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4.5vw, 4rem)', fontWeight: 400, color: T.bg, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 24, maxWidth: '15ch' }}>
                Can't see it<br/>in stock?<br/><em style={{ color: T.accent }}>We'll find it.</em>
              </h2>
              <p style={{ fontSize: 'var(--step-body)', color: 'rgba(232,230,225,0.6)', lineHeight: 1.75, maxWidth: '44ch', marginBottom: 36 }}>
                Tell us exactly what you want — make, model, spec, colour, budget — and we'll go and find it in the UK. We handle the entire process from search to keys.
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Link to="/import"><Button variant="primary" size="lg">Request a custom import</Button></Link>
                <Link to="/contact"><Button variant="outlineGold" size="lg">Talk to us first</Button></Link>
              </div>
            </div>
            {/* 4-step grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, background: 'rgba(232,230,225,0.06)' }}>
              {[
                ['01', 'You tell us', 'Make, spec, budget, timeline. The more detail the better.'],
                ['02', 'We search',   'UK dealers and five auction houses — we find the match.'],
                ['03', 'You approve', 'Full photos and details before any money changes hands.'],
                ['04', 'Keys in hand','Transport, VRT, NCT prep, registration. All included.'],
              ].map(([n, h, b]) => (
                <div key={n} style={{ padding: '28px 24px', background: 'rgba(26,25,22,0.6)', borderTop: `1px solid rgba(232,230,225,0.08)` }}>
                  <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-lead)', color: T.accent, fontWeight: 400, marginBottom: 10 }}>{n}</div>
                  <div style={{ fontSize: 'var(--step-sm)', fontWeight: 600, color: T.bg, marginBottom: 8, letterSpacing: '0.02em' }}>{h}</div>
                  <div style={{ fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.5)', lineHeight: 1.6 }}>{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ TESTIMONIALS ══════════════ */}
      <section style={{ paddingBlock: 'clamp(60px, 10vw, 120px)', background: T.surface }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)' }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 48 }}>What our customers say</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 80, alignItems: 'start' }}>
            {/* Portrait */}
            <div>
              <div style={{ aspectRatio: '3/4', background: T.bg, overflow: 'hidden', position: 'relative' }}>
                <img src="https://picsum.photos/id/187/600/800" alt="Atmosphere" loading="lazy" width="600" height="800"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(30%) brightness(0.8)', display: 'block' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 20, background: 'linear-gradient(to top, rgba(26,25,22,0.85), transparent)' }}>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {'★★★★★'.split('').map((s,i) => <span key={i} style={{ color: T.accent, fontSize: 16 }}>{s}</span>)}
                  </div>
                </div>
              </div>
              <p style={{ fontSize: 10, color: T.muted, marginTop: 6, fontStyle: 'italic' }}>Photo: Andre Koch — Unsplash</p>
            </div>
            {/* Quotes */}
            <div style={{ paddingTop: 40 }}>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} style={{ display: i === tIdx ? 'block' : 'none', animation: 'fadeUp 0.4s ease' }}>
                  <blockquote style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.4rem, 3vw, 2.2rem)', fontWeight: 400, lineHeight: 1.35, letterSpacing: '-0.015em', color: T.ink, marginBottom: 32, fontStyle: 'italic', maxWidth: '26ch' }}>
                    "{t.q}"
                  </blockquote>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 44, height: 44, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.bg, fontWeight: 600, fontSize: 'var(--step-body)', flexShrink: 0 }}>{t.name[0]}</div>
                    <div>
                      <div style={{ fontSize: 'var(--step-sm)', fontWeight: 600, color: T.ink }}>{t.name}</div>
                      <div style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em' }}>{t.loc}</div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Dots */}
              <div style={{ display: 'flex', gap: 8, marginTop: 40 }}>
                {TESTIMONIALS.map((_, i) => (
                  <button key={i} onClick={() => setTIdx(i)}
                    style={{ width: i === tIdx ? 28 : 8, height: 8, background: i === tIdx ? T.ink : T.surface, border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.3s' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ BOTTOM CTA ══════════════ */}
      <section style={{ paddingBlock: 'clamp(60px, 8vw, 100px)', background: T.accent }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 28 }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem, 4vw, 3rem)', fontWeight: 400, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.02em', maxWidth: '22ch' }}>
            Ready to find your next car?
          </h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/stock"><Button variant="dark" size="lg">Browse stock</Button></Link>
            <Link to="/contact"><Button variant="light" size="lg">Contact us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
